import { useEffect, useState } from "react";
import { CalendarPlus, Loader2 } from "lucide-react";
import { MailModalOverlay } from "@/features/shared/MailModalOverlay";
import {
  agendaIcsSavedMessage,
  previewAgendaIcs,
  readMailIcsText,
  saveAgendaIcs,
  type AgendaIcsPreview,
} from "@/lib/mail/importAgendaIcs";
import type { MailAttachmentLocation } from "@/lib/mail/saveMailAttachmentToSuite";
import type { MailAttachment } from "@/lib/types/mail";

type Props = {
  open: boolean;
  filename: string;
  location: MailAttachmentLocation;
  file: MailAttachment | null;
  onClose: () => void;
  onDone: (message: string) => void;
};

export function AgendaIcsImportModal({ open, filename, location, file, onClose, onDone }: Props) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<AgendaIcsPreview | null>(null);
  const [ics, setIcs] = useState<string | null>(null);
  const [calendarId, setCalendarId] = useState("");
  const accountId = location.accountId;
  const folder = location.folder;
  const messageUid = location.messageUid;

  useEffect(() => {
    if (!open || !file) return;
    let cancelled = false;
    setLoading(true);
    setSaving(false);
    setError(null);
    setPreview(null);
    setIcs(null);
    setCalendarId("");

    void (async () => {
      const text = await readMailIcsText({ accountId, folder, messageUid }, file);
      if (cancelled) return;
      if (!text.ok) {
        setError(text.error);
        setLoading(false);
        return;
      }
      const next = await previewAgendaIcs(text.ics);
      if (cancelled) return;
      if (!next.ok) {
        setError(next.error);
        setLoading(false);
        return;
      }
      setIcs(text.ics);
      setPreview(next.preview);
      setCalendarId(next.preview.defaultCalendarId ?? next.preview.calendars[0]?.id ?? "");
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [open, file, accountId, folder, messageUid]);

  const already = preview?.already === true;
  const calendars = preview?.calendars ?? [];
  const canSave = !loading && !saving && !already && ics != null && calendarId !== "" && calendars.length > 0;

  async function handleSave() {
    if (!ics || !canSave) return;
    setSaving(true);
    setError(null);
    const result = await saveAgendaIcs(ics, calendarId);
    if (!result.ok) {
      setSaving(false);
      setError(result.error);
      return;
    }
    onDone(agendaIcsSavedMessage(result.saved));
  }

  const storedDiffers =
    already &&
    preview?.storedWhenLabel &&
    preview.storedWhenLabel !== preview.event.whenLabel;

  return (
    <MailModalOverlay
      open={open}
      onClose={onClose}
      labelledBy="agenda-ics-title"
      variant="center"
      panelClassName="mail-move-dialog max-w-lg"
    >
      <div className="flex max-h-[min(78vh,32rem)] flex-col">
        <div className="mail-move-dialog-header">
          <div className="mail-move-dialog-icon" aria-hidden>
            <CalendarPlus className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="agenda-ics-title" className="truncate text-sm font-semibold text-[var(--mail-text)]">
              Enregistrer dans l’agenda
            </h2>
            <p className="mt-0.5 truncate text-[11px] text-[var(--mail-muted)]">{filename}</p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-[var(--mail-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              Lecture de l’invitation…
            </div>
          ) : preview ? (
            <div className="grid gap-3 text-sm text-[var(--mail-text)]">
              <div>
                <p className="text-base font-semibold leading-snug">{preview.event.title}</p>
                {preview.event.whenLabel ? (
                  <p className="mt-1 text-[var(--mail-muted)]">{preview.event.whenLabel}</p>
                ) : null}
                {preview.event.location ? (
                  <p className="mt-1 text-[var(--mail-muted)]">{preview.event.location}</p>
                ) : null}
              </div>

              {already ? (
                <p className="rounded-lg bg-[var(--mail-accent-soft)] px-3 py-2 text-xs leading-relaxed text-[var(--mail-text)]">
                  Ce rendez-vous est déjà dans « {preview.alreadyCalendarName || "votre agenda"} ».
                  {storedDiffers ? ` Version enregistrée : ${preview.storedWhenLabel}.` : ""}
                </p>
              ) : calendars.length === 0 ? (
                <p className="text-xs leading-relaxed text-[var(--mail-muted)]">
                  Aucun agenda personnel. Créez-en un dans Agenda.
                </p>
              ) : calendars.length === 1 ? (
                <p className="text-xs text-[var(--mail-muted)]">
                  Agenda : <span className="font-medium text-[var(--mail-text)]">{calendars[0].name}</span>
                </p>
              ) : (
                <label className="grid gap-1.5 text-xs text-[var(--mail-muted)]">
                  Agenda
                  <select
                    className="h-10 rounded-md border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] px-3 text-sm text-[var(--mail-text)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)]/40"
                    value={calendarId}
                    onChange={(event) => setCalendarId(event.target.value)}
                    disabled={saving}
                  >
                    {calendars.map((calendar) => (
                      <option key={calendar.id} value={calendar.id}>
                        {calendar.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          ) : null}

          {error ? (
            <p className="mt-3 text-xs text-[var(--mail-danger-text)]" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <div className="mail-move-dialog-footer gap-2">
          <button type="button" onClick={onClose} disabled={saving} className="mail-move-dialog-cancel">
            {already || (!loading && !preview) ? "Fermer" : "Annuler"}
          </button>
          {preview && !already ? (
            <button
              type="button"
              className="mail-btn-primary rounded-full px-4 py-1.5 text-sm font-medium disabled:opacity-50"
              onClick={() => void handleSave()}
              disabled={!canSave}
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          ) : null}
        </div>
      </div>
    </MailModalOverlay>
  );
}
